import { Modal } from "react-bootstrap";
import { Button } from "./Controls/Button";
import {
  InfoCircle,
  Calendar3,
  CreditCard2Back,
  ArrowRepeat,
  Receipt,
  CreditCard,
  ExclamationTriangle,
} from "react-bootstrap-icons";

type Props = {
  show: boolean;
  onHide: () => void;
  isAccountsReceivable: boolean;
};

export function BillingInfoModal({ show, onHide, isAccountsReceivable }: Props) {
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <InfoCircle className="text-primary" />
          How Billing Works
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        {isAccountsReceivable ? (
          <InvoicedPlanInfo />
        ) : (
          <AutoPayPlanInfo />
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button className="btn-main" onClick={onHide}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function InvoicedPlanInfo() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="bg-primary-subtle rounded p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Receipt className="text-primary fs-5" />
          <span className="fw-semibold">Your Plan is Billed by Invoice</span>
        </div>
        <p className="mb-0 text-muted small">
          You'll receive an invoice for each billing period. Here's how changes to your plan are handled:
        </p>
      </div>

      <div className="d-flex flex-column gap-3">
        <BillingScenario
          icon={<Calendar3 className="text-warning" />}
          title="Changes Before Payment"
          description="If you make changes to your plan before the billing period starts and before your invoice has been paid, you'll receive an amended invoice reflecting the updated charges."
          highlight="warning"
        />

        <BillingScenario
          icon={<CreditCard2Back className="text-success" />}
          title="Changes After Payment"
          description="Any changes made after you've already paid your invoice will be calculated and included on your next month's invoice."
          highlight="success"
        />

        <BillingScenario
          icon={<ArrowRepeat className="text-info" />}
          title="Ongoing Billing"
          description="Your billing cycle continues each month. The cost breakdown in Billing shows your estimated charges for the current and upcoming periods based on your current plan configuration."
          highlight="info"
        />

        <BillingScenario
          icon={<CreditCard className="text-primary" />}
          title="Switch to Auto-Pay"
          description="Want to skip the invoices? You can convert to automatic payments at any time by adding a payment method to your account. Once added, your future billing periods will be charged automatically."
          highlight="info"
        />
      </div>

      <div className="bg-warning-subtle rounded p-3 mt-2 d-flex align-items-start gap-2">
        <ExclamationTriangle className="text-warning flex-shrink-0 mt-1" />
        <div>
          <div className="fw-semibold mb-1">Important Note</div>
          <p className="mb-0 small text-muted">
            If you switch to auto-pay, any outstanding invoice balances must be settled separately. Adding a payment method does not automatically pay previous invoices.
          </p>
        </div>
      </div>

      <div className="bg-light rounded p-3 mt-2">
        <div className="fw-semibold mb-1">Quick Summary</div>
        <ul className="mb-0 small text-muted ps-3">
          <li>Before payment: Amended invoice sent</li>
          <li>After payment: Adjustments on next invoice</li>
          <li>Add a payment method to convert to auto-pay</li>
        </ul>
      </div>
    </div>
  );
}

function AutoPayPlanInfo() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="bg-primary-subtle rounded p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <CreditCard2Back className="text-primary fs-5" />
          <span className="fw-semibold">Your Plan Renews Automatically</span>
        </div>
        <p className="mb-0 text-muted small">
          Your payment method is charged automatically at each renewal. You can also purchase plan changes in real time. Here's how it works:
        </p>
      </div>

      <div className="d-flex flex-column gap-3">
        <BillingScenario
          icon={<Calendar3 className="text-warning" />}
          title="Changes During the Current Period"
          description="When you add parkers or increase capacity, you can check out and pay right away. Those changes are applied immediately (often with a prorated charge for the remainder of the period) so new parkers can start without waiting for the next renewal."
          highlight="warning"
        />

        <BillingScenario
          icon={<ArrowRepeat className="text-success" />}
          title="Next Renewal"
          description="When your plan renews, your payment method is charged for the upcoming period based on your plan at that time—including parkers and spaces you've already added."
          highlight="success"
        />
      </div>

      <div className="bg-light rounded p-3 mt-2">
        <div className="fw-semibold mb-1">Quick Summary</div>
        <ul className="mb-0 small text-muted ps-3">
          <li>Checkout now: Pay immediately and updates take effect right away</li>
          <li>Renewal: Charged for the next period based on your current plan</li>
        </ul>
      </div>
    </div>
  );
}

type BillingScenarioProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight: "warning" | "success" | "info";
};

function BillingScenario({
  icon,
  title,
  description,
  highlight,
}: BillingScenarioProps) {
  const borderClass = `border-start border-${highlight} border-3`;

  return (
    <div className={`${borderClass} ps-3 py-1`}>
      <div className="d-flex align-items-center gap-2 mb-1">
        {icon}
        <span className="fw-semibold">{title}</span>
      </div>
      <p className="mb-0 small text-muted">{description}</p>
    </div>
  );
}
